import { Monitor, Moon, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/components/ui/Button/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/DropdownMenu/DropdownMenu';
import { useTheme } from '@/shared/theme/ThemeProvider/ThemeProvider';
import { THEME_PREFERENCES } from '@/shared/store/reducers/preferences/preferences.types';

const ICON = { light: Sun, dark: Moon, system: Monitor } as const;

/**
 * Light / dark / follow-the-OS.
 *
 * Three options rather than a two-state switch, because `system` is a real
 * preference and collapsing it into a boolean means an operator who wants to
 * track their OS cannot say so. The trigger shows the RESOLVED icon, so it
 * reflects what is actually on screen.
 */
export function ThemeToggle() {
  const { t } = useTranslation('common');
  const { preference, theme, setPreference } = useTheme();
  const ResolvedIcon = ICON[theme];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={t('shell.toggleTheme')}>
          <ResolvedIcon aria-hidden className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        {THEME_PREFERENCES.map((option) => {
          const Icon = ICON[option];
          return (
            <DropdownMenuItem
              key={option}
              onSelect={() => {
                setPreference(option);
              }}
              // A menu that does not say which option is live makes the operator
              // click one to find out.
              aria-current={preference === option ? 'true' : undefined}
              className={preference === option ? 'bg-secondary' : undefined}
            >
              <Icon aria-hidden className="h-4 w-4" />
              {t(`theme.${option}`)}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
